// Story 10.2 — Formato relativo en español. Basado en el helper del CLI
// (Stories 6.5/6.6) re-escrito para el frontend (sin deps).

export interface RelativeTimeOptions {
  /** Reloj inyectable para tests determinísticos. */
  now?: Date;
  /** Si la diferencia es menor a `nowThresholdMs`, retorna "justo ahora". */
  nowThresholdMs?: number;
}

const SEC = 1000;
const MIN = 60 * SEC;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY; // aprox.
const YEAR = 365 * DAY; // aprox.

const FORMS: Array<[number, (n: number) => string]> = [
  [YEAR, (n) => (n === 1 ? 'hace 1 año' : `hace ${n} años`)],
  [MONTH, (n) => (n === 1 ? 'hace 1 mes' : `hace ${n} meses`)],
  [WEEK, (n) => (n === 1 ? 'hace 1 semana' : `hace ${n} semanas`)],
  [DAY, (n) => (n === 1 ? 'hace 1 día' : `hace ${n} días`)],
  [HOUR, (n) => (n === 1 ? 'hace 1 hora' : `hace ${n} horas`)],
  [MIN, (n) => (n === 1 ? 'hace 1 minuto' : `hace ${n} minutos`)],
  [SEC, (n) => (n === 1 ? 'hace 1 segundo' : `hace ${n} segundos`)],
];

const FUTURE_FORMS: Array<[number, (n: number) => string]> = [
  [YEAR, (n) => (n === 1 ? 'en 1 año' : `en ${n} años`)],
  [MONTH, (n) => (n === 1 ? 'en 1 mes' : `en ${n} meses`)],
  [WEEK, (n) => (n === 1 ? 'en 1 semana' : `en ${n} semanas`)],
  [DAY, (n) => (n === 1 ? 'en 1 día' : `en ${n} días`)],
  [HOUR, (n) => (n === 1 ? 'en 1 hora' : `en ${n} horas`)],
  [MIN, (n) => (n === 1 ? 'en 1 minuto' : `en ${n} minutos`)],
  [SEC, (n) => (n === 1 ? 'en 1 segundo' : `en ${n} segundos`)],
];

/**
 * Formato "hace N (segundos|minutos|horas|días|...)" o "en N (...)".
 * Acepta `Date`, ISO string, o epoch ms. Strings no parseables → 'fecha desconocida'.
 */
export function formatRelative(
  input: Date | string | number | null | undefined,
  options: RelativeTimeOptions = {},
): string {
  if (input == null) return 'fecha desconocida';
  const target = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(target.getTime())) return 'fecha desconocida';
  const now = options.now ?? new Date();
  const diff = now.getTime() - target.getTime();
  const threshold = options.nowThresholdMs ?? 5 * SEC;
  if (Math.abs(diff) < threshold) return 'justo ahora';
  const isPast = diff > 0;
  const abs = Math.abs(diff);
  const forms = isPast ? FORMS : FUTURE_FORMS;
  for (const [unitMs, format] of forms) {
    if (abs >= unitMs) {
      return format(Math.floor(abs / unitMs));
    }
  }
  return 'justo ahora';
}
